/* eslint-disable import/no-named-as-default-member */
import jalaliPlugin from 'jalali-plugin-dayjs';
import dayjs from 'dayjs';
import customFormat from 'dayjs/plugin/customParseFormat';
import duration, { type DurationUnitsObjectType } from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import { CalendarType } from '@utils/itinerary';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(customFormat);
dayjs.extend(duration);
dayjs.extend(jalaliPlugin);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type FormatterConfig =
    | {
          format: 'long';
          includeYear?: boolean;
          includeWeekday?: boolean;
          includeTime?: boolean;
          includeDate?: true;
          separator?: never;
      }
    | {
          format?: 'short';
          includeYear?: false;
          includeWeekday?: false;
          includeTime?: boolean;
          includeDate?: boolean;
          separator?: string;
      };

export type DateFormat = 'long' | 'short';

export function formatter(config?: FormatterConfig): string {
    const {
        format = 'short',
        includeDate = true,
        includeTime = false,
        includeWeekday = false,
        includeYear = false,
        separator = '-',
    } = config || {};
    switch (format) {
        case 'short':
            return (
                (includeDate ? `YYYY${separator}MM${separator}DD` : '') +
                (includeTime && includeDate ? ' ' : '') +
                (includeTime ? 'HH:mm' : '')
            );
        case 'long':
            return (
                `${includeWeekday ? 'dddd ' : ''}D` +
                ` ` +
                `MMMM${includeYear ? ' ' + 'YYYY' : ''} ${
                    includeTime ? 'HH:mm' : ''
                }`
            );
        default:
            return '';
    }
}

const day = reactive({
    js: (...params: Parameters<typeof dayjs>) =>
        dayjs(...params)
            .calendar(CalendarType.Jalali)
            .locale('fa'),
    calendar: CalendarType.Jalali as CalendarType,
    locale: 'fa' as 'fa' | 'en',
    // non-reactive helpers, just to unify
    jalali: (...params: Parameters<typeof dayjs>) =>
        dayjs(...params)
            .calendar(CalendarType.Jalali)
            .locale('fa'),
    gregory: (...params: Parameters<typeof dayjs>) =>
        dayjs(...params)
            .calendar(CalendarType.Gregorian)
            .locale('fa'),
    jalaliEn: (...params: Parameters<typeof dayjs>) =>
        dayjs(...params)
            .calendar(CalendarType.Jalali)
            .locale('en'),
    gregoryEn: (...params: Parameters<typeof dayjs>) =>
        dayjs(...params)
            .calendar(CalendarType.Gregorian)
            .locale('en'),
    duration: (params: DurationUnitsObjectType) => dayjs.duration(params),
});

export type DateUtil = typeof day;

const switchCalendar = () => {
    let newCalendar: CalendarType;
    switch (day.calendar) {
        case CalendarType.Gregorian:
            newCalendar = CalendarType.Jalali;
            break;
        case CalendarType.Jalali:
        default:
            newCalendar = CalendarType.Gregorian;
            break;
    }
    day.calendar = newCalendar;
    day.js = (...params: any) =>
        dayjs(params).calendar(newCalendar).locale(day.locale);
    window.localStorage.setItem('calendar', newCalendar);
};

const switchLocale = () => {
    let newLocale: 'en' | 'fa';
    switch (day.locale) {
        case 'fa':
            newLocale = 'en';
            break;
        case 'en':
        default:
            newLocale = 'fa';
            break;
    }
    day.locale = newLocale;
    day.js = (...params: any) =>
        dayjs(params).calendar(day.calendar).locale(newLocale);
    window.localStorage.setItem('locale', newLocale);
};
export { switchCalendar, switchLocale, day as dayConfig };
